import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdSelector from "../Customer/AdSelector";
import { useCart } from "../DeviceRequests/CartContext";
import { API_BASE_URL } from "../../config";
import HourPicker from "./HourPicker";
import "../../styles/Browse.css";

// This form used to require a "Proposed Rate (USD)" the customer typed in.
// The server prices a booking from Device.daily_rate and ignores that field
// entirely, so the number was either ignored or -- before the server-side fix
// -- was the price, which meant typing 0.01 bought a screen-day for a cent.
// What belongs here is the screen's actual rate and the total it works out to.

function money(value) {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

// Bookings are a contiguous run of HOURS, half-open: 9am-5pm occupies nine
// through four and releases five. The hours come from HourPicker, which reads
// the screen's own availability, so what is offered here is what the server
// will accept -- the point being that a refusal happens before payment rather
// than as a 409 at checkout.
export default function CreateDeviceRequest({ deviceId, device: deviceProp }) {
    const [ads, setAds] = useState([]);
    const [device, setDevice] = useState(deviceProp || null);
    const [selectedAdId, setSelectedAdId] = useState("");
    // What the picker hands back: {start_time, end_time, hours,
    // estimated_cents}, or null when nothing is selected.
    const [slot, setSlot] = useState(null);
    const [status, setStatus] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const { refreshCart } = useCart();

    const token = localStorage.getItem("token");

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/customer/ads`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setAds(data.images || []))
            .catch(() => setAds([]));
    }, [token]);

    // The parent usually already has the device; fetch only if it does not, so
    // the rate shown is never guessed.
    useEffect(() => {
        if (deviceProp) { setDevice(deviceProp); return; }
        let cancelled = false;
        fetch(`${API_BASE_URL}/api/devices/summary/${deviceId}`)
            .then((res) => res.json())
            .then((data) => { if (!cancelled) setDevice(data.device || null); })
            .catch(() => { if (!cancelled) setDevice(null); });
        return () => { cancelled = true; };
    }, [deviceId, deviceProp]);

    const rate = device && device.daily_rate != null ? Number(device.daily_rate) : null;


    async function handleSubmit(e) {
        e.preventDefault();
        if (!selectedAdId) { setStatus("Choose an ad to run."); return; }
        if (!slot) { setStatus("Choose the hours you want on this screen."); return; }

        // Hour-aligned and half-open, matching booking.covered_hours() on the
        // server: 9am-5pm occupies nine through four and releases five. The
        // picker works in the SCREEN'S local time and hands back UTC, which is
        // the only representation that survives a DST boundary.
        const { start_time, end_time } = slot;

        setSubmitting(true);
        setStatus("Adding to cart…");
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/cart`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                // No rate is sent: the cart snapshots the device's own rate.
                body: JSON.stringify({
                    device_id: deviceId,
                    image_id: selectedAdId,
                    start_time,
                    end_time,
                }),
            });
            if (res.ok) {
                refreshCart();
                navigate("/customer/cart");
            } else {
                const data = await res.json().catch(() => ({}));
                setStatus(data.message || "Could not add this to your cart.");
            }
        } catch {
            setStatus("Network error. Nothing was added.");
        }
        setSubmitting(false);
    }

    if (rate === null) {
        return (
            <div className="bk">
                <div className="wb-note wb-note--warn">
                    This screen has no rate set yet, so it cannot be booked. Try again later.
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="bk">
            <h2>Book this screen</h2>

            {/* The date range and day count are gone. A screen is sold by the
                hour now, and the picker below is the only place that knows
                which hours are actually on air and unsold -- a pair of date
                inputs could express "the 14th" but not "the 14th, except the
                afternoon somebody else already bought". */}
            <HourPicker deviceId={deviceId} onChange={setSlot} />

            <div className="bk__quote">
                <div className="bk__quote-row">
                    <span>Day rate (24 hours)</span><span>{money(rate)}</span>
                </div>
                <div className="bk__quote-row">
                    <span>Hours</span><span>{slot ? slot.hours : "—"}</span>
                </div>
                <div className="bk__quote-total">
                    <span>Total</span>
                    <span>
                        {slot && slot.estimated_cents != null
                            ? money(slot.estimated_cents / 100)
                            : "—"}
                    </span>
                </div>
            </div>

            <div className="bk__ads">
                <h3>Choose an ad</h3>
                <AdSelector ads={ads} selectedAdId={selectedAdId} onSelect={setSelectedAdId} />
            </div>

            <button
                type="submit"
                className="wb-btn wb-btn--primary wb-btn--lg"
                disabled={submitting}
            >
                {submitting ? "Adding…" : "Add to cart"}
            </button>

            {status && <div className="wb-note wb-note--danger" style={{ marginTop: "1rem" }}>{status}</div>}
        </form>
    );
}
